package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.components.motion.Disclosure
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertCircle
import co.maytes.standupbot.dashboard.styles.Layout
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.SettingsClasses
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private class RowCounter(var next: Int = 0)

private val LocalRowCounter = compositionLocalOf { RowCounter() }

private const val ENTER_STEP_MS = 40
private const val ENTER_CAP_MS = 240

@Composable
fun SettingsPage(
    group: SettingsGroup,
    title: String,
    subtitle: (@Composable () -> Unit)? = null,
    action: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val counter = remember { RowCounter() }
    CompositionLocalProvider(LocalRowCounter provides counter) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Row(
                modifier = Modifier.margin(bottom = Spacing.xxs),
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
            ) {
                SpanText(
                    text = "Settings",
                    modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary),
                )
                SpanText(
                    text = "/",
                    modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary),
                )
                SpanText(
                    text = group.label,
                    modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary),
                )
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(bottom = Spacing.lg)
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("gap", Spacing.md.toString())
                    },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top,
            ) {
                Column(modifier = Modifier.styleModifier { property("min-width", "0") }) {
                    SpanText(
                        text = title,
                        modifier = Modifier
                            .fontSize(Typography.h2.size)
                            .fontWeight(Typography.h2.weight)
                            .color(Palette.textPrimary)
                            .styleModifier {
                                property("letter-spacing", "-0.015em")
                                property("line-height", Typography.h2.lineHeight.toString())
                            },
                    )
                    if (subtitle != null) {
                        Box(modifier = Modifier.margin(top = Spacing.xxs)) { subtitle() }
                    }
                }
                if (action != null) Box { action() }
            }
            content()
        }
    }
}

@Composable
fun SettingsSubtitle(content: @Composable () -> Unit) {
    P(
        attrs = Modifier
            .fontSize(Typography.label.size)
            .color(Palette.textSecondary)
            .styleModifier {
                property("margin", "0")
                property("max-width", "64ch")
                property("line-height", "1.5")
            }
            .toAttrs(),
    ) { content() }
}

@Composable
fun Plain(text: String) {
    Span { Text(text) }
}

@Composable
fun Strong(text: String) {
    Span(attrs = Modifier.color(Palette.textPrimary).fontWeight(500).toAttrs()) { Text(text) }
}

@Composable
fun SettingsSectionRow(
    anchor: String,
    title: String,
    description: String? = null,
    aside: List<Pair<String, String>> = emptyList(),
    content: @Composable () -> Unit,
) {
    val counter = LocalRowCounter.current
    val delayMs = remember { (counter.next++ * ENTER_STEP_MS).coerceAtMost(ENTER_CAP_MS) }
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .classNames(Motion.ENTER_CLASS)
            .padding(topBottom = Spacing.xl)
            .styleModifier {
                property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
                if (delayMs > 0) property("animation-delay", "${delayMs}ms")
            },
    ) {
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .classNames(SettingsClasses.ROW)
                .toAttrs { attr("id", settingsAnchorId(anchor)) },
        ) {
            Column(modifier = Modifier.styleModifier { property("min-width", "0") }) {
                SpanText(
                    text = title,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary)
                        .margin(top = Spacing.xxs),
                )
                if (description != null) {
                    SpanText(
                        text = description,
                        modifier = Modifier
                            .margin(top = Spacing.xxs)
                            .fontSize(Typography.label.size)
                            .color(Palette.textSecondary)
                            .styleModifier { property("max-width", "36ch") },
                    )
                }
                if (aside.isNotEmpty()) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .margin(top = Spacing.md)
                            .styleModifier {
                                property("max-width", "36ch")
                                property("gap", Spacing.xxs.toString())
                            },
                    ) {
                        aside.forEach { (key, value) ->
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                            ) {
                                SpanText(
                                    text = key,
                                    modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textTertiary),
                                )
                                SpanText(
                                    text = value,
                                    modifier = Modifier
                                        .fontSize(Typography.bodySmall.size)
                                        .fontWeight(500)
                                        .color(Palette.textSecondary),
                                )
                            }
                        }
                    }
                }
            }
            Column(modifier = Modifier.styleModifier { property("min-width", "0") }) { content() }
        }
    }
}

fun settingsAnchorId(anchor: String): String = "settings-$anchor"

@Composable
fun SettingsPanel(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.panel)
            .borderRadius(Radius.xl)
            .padding(Spacing.md)
            .styleModifier { property("gap", Spacing.md.toString()) }
            .then(modifier),
    ) { content() }
}

@Composable
fun SettingsField(
    label: String,
    modifier: Modifier = Modifier,
    help: String? = null,
    error: String? = null,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("gap", Spacing.xs.toString()) }
            .then(modifier),
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.label.size)
                .fontWeight(Typography.label.weight)
                .color(Palette.textPrimary),
        )
        content()
        if (error != null) {
            SpanText(
                text = error,
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.status(StatusHue.Danger).solid),
            )
        } else if (help != null) {
            SpanText(
                text = help,
                modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
            )
        }
    }
}

@Composable
fun SettingsRowItem(
    title: String,
    description: String? = null,
    first: Boolean = false,
    titleTrailing: (@Composable () -> Unit)? = null,
    leading: (@Composable () -> Unit)? = null,
    trailing: (@Composable () -> Unit)? = null,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .let { if (first) it else it.padding(top = Spacing.sm) }
            .styleModifier {
                if (!first) property("border-top", "1px solid ${Palette.border.cssRgb()}")
                property("gap", Spacing.md.toString())
                property("flex-wrap", "wrap")
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        leading?.invoke()
        Column(modifier = Modifier.styleModifier { property("min-width", "0"); property("flex", "1 1 ${Layout.rowTextBasis}") }) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
            ) {
                SpanText(
                    text = title,
                    modifier = Modifier
                        .fontSize(Typography.label.size)
                        .fontWeight(Typography.label.weight)
                        .color(Palette.textPrimary),
                )
                titleTrailing?.invoke()
            }
            if (description != null) {
                SpanText(
                    text = description,
                    modifier = Modifier
                        .margin(top = Spacing.xxs)
                        .fontSize(Typography.label.size)
                        .fontWeight(400)
                        .color(Palette.textSecondary)
                        .styleModifier { property("overflow-wrap", "anywhere") },
                )
            }
        }
        if (trailing != null) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(Spacing.xs),
                modifier = Modifier.styleModifier {
                    property("flex-shrink", "0")
                    property("margin-left", "auto")
                },
            ) { trailing() }
        }
    }
}

@Composable
fun SettingsPreview(
    label: String,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.surface)
            .borderRadius(Radius.lg)
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .styleModifier { property("border", "1px solid ${Palette.borderSubtle.cssRgb()}") }
            .then(modifier),
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(400)
                .color(Palette.textTertiary)
                .margin(bottom = Spacing.xxs),
        )
        P(
            attrs = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary)
                .styleModifier { property("margin", "0") }
                .toAttrs(),
        ) { content() }
    }
}

@Composable
fun SettingsSaveBar(
    open: Boolean,
    saving: Boolean,
    hint: String,
    onSave: () -> Unit,
    onReset: () -> Unit,
    canSave: Boolean = true,
    saveLabel: String = "Save",
) {
    Disclosure(open = open, modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = Spacing.md)
                .styleModifier { property("gap", Spacing.xs.toString()) },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = hint,
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary)
                    .styleModifier { property("margin-right", "auto") },
            )
            Button(
                label = "Reset",
                variant = ButtonVariant.Ghost,
                size = ButtonSize.Sm,
                enabled = !saving,
                onClick = onReset,
            )
            Button(
                label = if (saving) "Saving" else saveLabel,
                variant = ButtonVariant.Primary,
                size = ButtonSize.Sm,
                enabled = canSave && !saving,
                loading = saving,
                onClick = onSave,
            )
        }
    }
}

@Composable
fun SettingsEmpty(text: String, action: (@Composable () -> Unit)? = null) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(Radius.lg)
            .padding(topBottom = Spacing.sm, leftRight = Spacing.md)
            .styleModifier {
                property("border", "1px dashed ${Palette.border.cssRgb()}")
                property("gap", Spacing.md.toString())
            },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        SpanText(
            text = text,
            modifier = Modifier.fontSize(Typography.label.size).fontWeight(400).color(Palette.textSecondary),
        )
        action?.invoke()
    }
}

@Composable
fun SettingsError(what: String, onRetry: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("gap", Spacing.sm.toString()) },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(modifier = Modifier.color(Palette.status(StatusHue.Danger).solid)) { LucideAlertCircle(size = 18) }
        SpanText(
            text = "Couldn’t load $what.",
            modifier = Modifier
                .fontSize(Typography.label.size)
                .fontWeight(400)
                .color(Palette.textPrimary)
                .styleModifier { property("flex", "1 1 auto") },
        )
        Button(label = "Retry", variant = ButtonVariant.Ghost, size = ButtonSize.Sm, onClick = onRetry)
    }
}

@Composable
fun SettingsBars(rows: Int, modifier: Modifier = Modifier) {
    Column(
        modifier = Modifier.fillMaxWidth().styleModifier { property("gap", Spacing.sm.toString()) }.then(modifier),
    ) {
        repeat(rows) { index ->
            Box(
                modifier = Modifier
                    .classNames(SettingsClasses.BAR)
                    .styleModifier {
                        property("height", Spacing.md.toString())
                        property("width", if (index % 2 == 0) "70%" else "45%")
                    },
            )
        }
    }
}

@Composable
fun RetentionWindowStrip(keptDays: Int) {
    val percent = (RetentionWindow.keptFraction(keptDays) * PERCENT_SCALE).coerceAtLeast(MIN_KEPT_PERCENT)
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", Spacing.xxs.toString()) }) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(Radius.md)
                .backgroundColor(Palette.border)
                .styleModifier {
                    property("position", "relative")
                    property("height", Spacing.xs.toString())
                    property("overflow", "hidden")
                },
        ) {
            Box(
                modifier = Modifier
                    .backgroundColor(Palette.accent)
                    .borderRadius(Radius.md)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "0")
                        property("right", "0")
                        property("bottom", "0")
                        property("width", "$percent%")
                        property("transition", Motion.transition(Motion.DUR_PILL, "width"))
                    },
            )
        }
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            SpanText(
                text = "${RetentionWindow.TRACK_DAYS} days ago",
                modifier = Modifier.fontSize(Typography.caption.size).fontWeight(400).color(Palette.textTertiary),
            )
            SpanText(
                text = "Today",
                modifier = Modifier.fontSize(Typography.caption.size).fontWeight(400).color(Palette.textTertiary),
            )
        }
    }
}

private const val PERCENT_SCALE = 100.0
private const val MIN_KEPT_PERCENT = 1.5
